import { useMemo } from "react";
import {
  maxChildren as maxChildrenLimit,
  maxGuests as maxGuestsLimit,
} from "../config";

export function useCapacityLimits(capacity, adults, childrenCount) {
  return useMemo(() => {
    if (!capacity) {
      return {
        maxAdults: maxGuestsLimit,
        maxChildren: maxChildrenLimit,
        canAddAdult: true,
        canAddChild: true,
      };
    }

    if (!Array.isArray(capacity?.extra_places)) {
      capacity.extra_places = [];
    }

    const { main_places, extra_places } = capacity;

    // 1. Размещаем взрослых на основных местах (приоритетно)
    const adultsOnMain = Math.min(adults, main_places);
    const remainingAdults = adults - adultsOnMain;
    const freeMainPlaces = main_places - adultsOnMain;

    // 2. Размещаем детей на оставшихся основных местах
    const childrenOnMain = Math.min(childrenCount, freeMainPlaces);
    const remainingChildren = childrenCount - childrenOnMain;
    const stillFreeMain = freeMainPlaces - childrenOnMain;

    // 3. Считаем доп. места по типам (исправлены опечатки!)
    const adultExtraTotal = extra_places.filter(
      (p) => p.type === "adult",
    ).length;
    const childExtraTotal = extra_places.filter(
      (p) => p.type === "child",
    ).length;
    const flexibleTotal = extra_places.filter(
      (p) => p.type === "adult_or_child",
    ).length;

    // 4. Размещаем оставшихся взрослых на доп. местах
    // Сначала на adult-местах, потом на flexible
    const adultsOnAdultExtra = Math.min(remainingAdults, adultExtraTotal);
    const adultsStillNeed = remainingAdults - adultsOnAdultExtra;
    const adultsOnFlexible = Math.min(adultsStillNeed, flexibleTotal);

    // Считаем занятые flexible места
    const flexibleUsedByAdults = adultsOnFlexible;
    const flexibleAvailableForChildren = flexibleTotal - flexibleUsedByAdults;

    // 5. Размещаем оставшихся детей
    // Сначала на flexible, потом на child-местах
    const childrenOnFlexible = Math.min(
      remainingChildren,
      flexibleAvailableForChildren,
    );
    const childrenStillNeed = remainingChildren - childrenOnFlexible;
    const childrenOnChildExtra = Math.min(childrenStillNeed, childExtraTotal);

    // 6. Считаем, что ещё свободно
    const freeAdultExtra = adultExtraTotal - adultsOnAdultExtra;
    const freeChildExtra = childExtraTotal - childrenOnChildExtra;
    const freeFlexible =
      flexibleTotal - flexibleUsedByAdults - childrenOnFlexible;

    // 7. Можно ли добавить взрослого?
    const canAddAdult =
      stillFreeMain > 0 || // Есть свободное основное место
      freeAdultExtra > 0 || // Есть свободное взрослое доп. место
      freeFlexible > 0; // Есть свободное flexible место

    // 8. Можно ли добавить ребёнка?
    const canAddChild =
      stillFreeMain > 0 || // Есть свободное основное место
      freeFlexible > 0 || // Есть свободное flexible место
      freeChildExtra > 0; // Есть свободное детское доп. место

    // 9. Максимальные значения для информации
    const maxAdults = main_places + adultExtraTotal + flexibleTotal;
    const maxChildren = main_places + childExtraTotal + flexibleTotal;
    const maxTotal = main_places + extra_places.length;

    return {
      maxAdults,
      maxChildren,
      maxTotal,
      canAddAdult,
      canAddChild,
      // Отладочная информация
      _debug: {
        main_places,
        adults,
        childrenCount,
        adultsOnMain,
        childrenOnMain,
        stillFreeMain,
        adultsOnAdultExtra,
        adultsOnFlexible,
        childrenOnFlexible,
        childrenOnChildExtra,
        freeAdultExtra,
        freeChildExtra,
        freeFlexible,
      },
    };
  }, [capacity, adults, childrenCount]);
}
